import type { Resource, ResourceChild } from './modules';
import type { Action } from './forms';
import { field, type Row, type GetPath } from './api/client';

const act = (
  row: Row,
  label: string,
  path: Action['path'],
  permission: string,
  method: Action['method'] = 'POST',
  operation?: string,
): Action => ({
  label,
  path,
  permission,
  method,
  operation,
  params: {
    id: row.id,
    accountId: row.id,
    employeeId: row.id,
    departmentId: row.id,
    teamId: row.id,
    positionId: row.id,
  },
  initial: row,
});
const allowed = (row: Row, key: string) =>
  Array.isArray(row.allowedActions) && row.allowedActions.includes(key);
const versionChildren = (row: Row, parent: string, version: string): ResourceChild => ({
  key: 'version-cards',
  title: 'История версий',
  path: `/api/v1/${parent}/{id}/versions` as GetPath,
  params: { id: row.id },
  fields: ['versionNumber', 'status', 'publishedAt'],
  targetResource: version,
});

export function applyBackendFeatures(resources: Resource[]) {
  const byKey = (key: string) => resources.find((resource) => resource.key === key)!;
  const extend = (key: string, extra: (row: Row) => Action[]) => {
    const resource = byKey(key);
    const original = resource.actions;
    resource.actions = (row) => {
      const actions = [...(original?.(row) || []), ...extra(row)];
      return actions.filter(
        (item, index) =>
          actions.findIndex(
            (other) =>
              other.path === item.path &&
              (other.method || 'POST') === (item.method || 'POST') &&
              other.operation === item.operation,
          ) === index,
      );
    };
  };
  byKey('templates').readPermissions = ['onboarding.read', 'offboarding.read'];
  byKey('templates').children = (row) => [
    {
      key: 'versions',
      title: 'Версии',
      path: '/api/v1/process-templates/{id}/versions',
      params: { id: row.id },
      fields: ['versionNumber', 'status', 'createdAt'],
      targetResource: 'process-template-versions',
    },
  ];
  resources.push({
    key: 'process-template-versions',
    section: 'hr',
    hidden: true,
    parentScoped: 'templates',
    title: 'Версии шаблона',
    singular: 'Версия шаблона процесса',
    path: '/api/v1/process-templates/{id}/versions',
    readPermission: 'onboarding.read',
    readPermissions: ['onboarding.read', 'offboarding.read'],
    fields: ['versionNumber', 'type', 'status', 'stages'],
    description: 'Этапы и задачи HR-процесса',
    actions: (row) =>
      field(row, 'status') === 'DRAFT'
        ? [
            act(
              row,
              'Опубликовать версию шаблона',
              '/api/v1/process-template-versions/{id}/publish',
              field(row, 'type') === 'OFFBOARDING' ? 'offboarding.manage' : 'onboarding.manage',
            ),
          ]
        : [],
  });
  extend('employees', (row) => [
    act(
      row,
      'Изменить рабочую почту',
      '/api/v1/employees/{employeeId}/change-work-email',
      'employees.edit',
    ),
    ...(field(row, 'status') === 'JOINING'
      ? [
          act(
            row,
            'Активировать сотрудника',
            '/api/v1/employees/{employeeId}/activate',
            'employees.edit',
          ),
        ]
      : []),
    ...(field(row, 'status') === 'INACTIVE'
      ? [
          act(
            row,
            'Повторно нанять сотрудника',
            '/api/v1/employees/{employeeId}/rehire',
            'employees.edit',
          ),
        ]
      : []),
    ...(field(row, 'status') === 'ARCHIVED'
      ? [
          act(
            row,
            'Восстановить из архива',
            '/api/v1/employees/{employeeId}/restore' as Action['path'],
            'employees.edit',
          ),
        ]
      : [
          act(
            row,
            'Архивировать сотрудника',
            '/api/v1/employees/{employeeId}/archive' as Action['path'],
            'employees.archive',
          ),
        ]),
    {
      ...act(
        row,
        'Удалить сотрудника',
        '/api/v1/employees/{employeeId}/delete' as Action['path'],
        'employees.delete',
      ),
      description:
        'Удаление доступно только для неиспользуемого профиля. Чтобы сохранить рабочую историю сотрудника, используйте архив.',
    },
  ]);
  extend('accounts', (row) => [
    ...(row.type !== 'SERVICE_ACCOUNT'
      ? [
          {
            ...act(
              row,
              'Изменить доступ к пространствам',
              '/api/v1/accounts/{accountId}/work-areas',
              'accounts.manage',
              'PATCH',
              'work-areas',
            ),
            requiredPermissions: ['permissions.manage'],
            description:
              'Доступ к HR и административному отделу назначается отдельно. Права на действия внутри пространства задаются группами доступа.',
          } satisfies Action,
        ]
      : []),
    ...(field(row, 'status') === 'DISABLED'
      ? [
          act(
            row,
            'Восстановить доступ',
            '/api/v1/accounts/{accountId}/reactivate',
            'accounts.manage',
          ),
        ]
      : [
          act(
            row,
            'Отключить учётную запись',
            '/api/v1/accounts/{accountId}/disable',
            'accounts.manage',
          ),
        ]),
    {
      ...act(
        row,
        'Назначить группу доступа',
        '/api/v1/accounts/{accountId}/permission-assignments/{assignmentId}',
        'permissions.manage',
        'PUT',
        'assign-group',
      ),
      generatedParameters: ['assignmentId'],
    },
    {
      ...act(
        row,
        'Добавить исключение прав',
        '/api/v1/accounts/{accountId}/permission-overrides',
        'permissions.manage',
      ),
      schemaOverrides: {
        entityType: {
          type: 'string',
          enum: ['Workspace', 'Organization', 'Department', 'Team', 'Employee', 'Account'],
        },
      },
    },
  ]);
  byKey('accounts').children = (row) => [
    {
      key: 'access-groups',
      title: 'Группы доступа',
      path: '/api/v1/accounts/{accountId}/access',
      params: { accountId: row.id },
      collection: 'assignments',
      permission: 'permissions.manage',
      fields: ['permissionGroupId', 'organizationId', 'createdAt'],
      actions: (child, parent) => [
        {
          ...act(
            parent,
            'Изменить назначение',
            '/api/v1/accounts/{accountId}/permission-assignments/{assignmentId}',
            'permissions.manage',
            'PUT',
            'edit-assignment',
          ),
          params: { accountId: parent.id, assignmentId: child.id },
          initial: child,
        },
        {
          ...act(
            parent,
            'Убрать группу',
            '/api/v1/accounts/{accountId}/permission-assignments/{assignmentId}',
            'permissions.manage',
            'DELETE',
            'remove-assignment',
          ),
          params: { accountId: parent.id, assignmentId: child.id },
        },
      ],
    },
    {
      key: 'access-overrides',
      title: 'Исключения прав',
      path: '/api/v1/accounts/{accountId}/access',
      params: { accountId: row.id },
      collection: 'overrides',
      permission: 'permissions.manage',
      fields: ['permission', 'effect', 'entityType', 'reason'],
      actions: (child, parent) => [
        {
          ...act(
            parent,
            'Убрать исключение',
            '/api/v1/accounts/{accountId}/permission-overrides/{overrideId}',
            'permissions.manage',
            'DELETE',
            'remove-override',
          ),
          params: { accountId: parent.id, overrideId: child.id },
        },
      ],
    },
    {
      key: 'session-management',
      title: 'Устройства и сессии',
      path: '/api/v1/accounts/{accountId}/sessions',
      params: { accountId: row.id },
      permission: 'accounts.manage',
      fields: ['createdAt', 'expiresAt', 'status'],
      actions: (child, parent) =>
        field(child, 'status') === 'ACTIVE'
          ? [
              {
                ...act(
                  parent,
                  'Завершить сессию',
                  '/api/v1/accounts/{accountId}/sessions/{sessionId}',
                  'accounts.manage',
                  'DELETE',
                  'revoke-session',
                ),
                params: { accountId: parent.id, sessionId: child.id },
              },
            ]
          : [],
    },
  ];
  for (const key of ['departments', 'teams', 'positions']) {
    const permission = key === 'teams' ? 'projects' : key;
    const id = key === 'departments' ? 'departmentId' : key === 'teams' ? 'teamId' : 'positionId';
    extend(key, (row) => [
      ...(field(row, 'status') !== 'ARCHIVED'
        ? [
            {
              ...act(
                row,
                'Архивировать',
                `/api/v1/${key}/{${id}}/archive` as Action['path'],
                `${permission}.archive`,
              ),
              description: 'Архив сохраняет запись и историю её использования.',
            },
          ]
        : [
            {
              ...act(
                row,
                'Восстановить из архива',
                `/api/v1/${key}/{${id}}/restore` as Action['path'],
                `${permission}.edit`,
              ),
              description: 'Запись снова станет доступна для новых назначений.',
            },
          ]),
      {
        ...act(
          row,
          'Удалить',
          `/api/v1/${key}/{${id}}/delete` as Action['path'],
          `${permission}.delete`,
          'POST',
          'delete',
        ),
        description:
          'Удаление доступно только для неиспользуемой записи. Если к ней привязаны сотрудники или другие данные, сначала переназначьте их. Для сохранения истории используйте архив.',
      },
    ]);
  }
  extend('requisitions', (row) => [
    ...(field(row, 'lifecycleStatus') === 'CLOSED'
      ? [act(row, 'Открыть повторно', '/api/v1/requisitions/{id}/reopen', 'requisitions.manage')]
      : []),
    ...(field(row, 'lifecycleStatus') !== 'ARCHIVED'
      ? [
          act(
            row,
            'Архивировать заявку',
            '/api/v1/requisitions/{id}/archive',
            'requisitions.manage',
          ),
        ]
      : []),
  ]);
  extend('job-postings', (row) =>
    ['OPEN', 'ON_HOLD', 'CLOSED'].includes(field(row, 'lifecycleStatus'))
      ? [
          ...(field(row, 'lifecycleStatus') === 'OPEN'
            ? [
                act(
                  row,
                  'Приостановить вакансию',
                  '/api/v1/job-postings/{id}/hold',
                  'job_postings.manage',
                ),
                act(
                  row,
                  'Закрыть вакансию',
                  '/api/v1/job-postings/{id}/close',
                  'job_postings.manage',
                ),
              ]
            : []),
          ...(field(row, 'lifecycleStatus') === 'ON_HOLD'
            ? [
                act(
                  row,
                  'Возобновить вакансию',
                  '/api/v1/job-postings/{id}/resume',
                  'job_postings.manage',
                ),
              ]
            : []),
          ...(field(row, 'lifecycleStatus') === 'CLOSED'
            ? [
                act(
                  row,
                  'Открыть повторно',
                  '/api/v1/job-postings/{id}/reopen',
                  'job_postings.manage',
                ),
              ]
            : []),
          act(
            row,
            'Архивировать вакансию',
            '/api/v1/job-postings/{id}/archive',
            'job_postings.manage',
          ),
        ]
      : [],
  );
  extend('candidates', (row) => [
    ...(Array.isArray(row.privacyNoticeOptions) && row.privacyNoticeOptions.length > 0
      ? [
          {
            ...act(
              row,
              'Зафиксировать предоставление уведомления',
              '/api/v1/candidates/{id}/privacy-notice-deliveries',
              'candidates.manage',
            ),
            choices: { '/api/v1/privacy-notice-versions': row.privacyNoticeOptions as Row[] },
          },
        ]
      : []),
    ...(row.canRecordConsent === true
      ? [act(row, 'Добавить согласие', '/api/v1/candidates/{id}/consents', 'candidates.manage')]
      : []),
  ]);
  byKey('candidates').children = (row) => [
    {
      key: 'consents',
      title: 'Согласия',
      path: '/api/v1/candidates/{id}/consents',
      params: { id: row.id },
      fields: ['purpose', 'grantedAt', 'revokedAt'],
      actions: (child, parent) =>
        child.revokedAt
          ? []
          : [
              {
                ...act(
                  parent,
                  'Отозвать согласие',
                  '/api/v1/candidate-consents/{id}/revoke',
                  'candidates.manage',
                ),
                params: { id: child.id },
                initial: child,
              },
            ],
    },
  ];
  for (const [parent, key, suffix, fields] of [
    [
      'job-postings',
      'application-form-cards',
      'application-form-versions',
      ['versionNumber', 'sections', 'status'],
    ],
    ['job-postings', 'pipeline-cards', 'pipeline-versions', ['versionNumber', 'stages', 'status']],
    [
      'offers',
      'offer-version-cards',
      'versions',
      ['versionNumber', 'title', 'body', 'compensation', 'startDate', 'expiresAt'],
    ],
  ] as const) {
    const resource = byKey(parent);
    const original = resource.children;
    resource.children = (row) => [
      ...(original?.(row) || []),
      {
        key,
        title:
          key === 'application-form-cards'
            ? 'Формы отклика'
            : key === 'pipeline-cards'
              ? 'Воронки найма'
              : 'Версии оффера',
        path: `/api/v1/${parent}/{id}/${suffix}` as GetPath,
        params: { id: row.id },
        fields: [
          'versionNumber',
          ...(parent === 'offers' ? ['title', 'createdAt'] : ['status', 'createdAt']),
        ],
        targetResource: key,
      },
    ];
    resources.push({
      key,
      section: 'recruitment',
      hidden: true,
      parentScoped: parent,
      title: 'Версии',
      singular: 'Версия',
      path: `/api/v1/${parent}/{id}/${suffix}` as GetPath,
      readPermission: resource.readPermission,
      fields: [...fields],
      description: 'Содержимое сохранённой версии',
    });
  }
  extend('applications', (row) =>
    ['REJECTED', 'WITHDRAWN', 'HIRED'].includes(field(row, 'status'))
      ? [act(row, 'Архивировать отклик', '/api/v1/applications/{id}/archive', 'candidates.manage')]
      : [],
  );
  extend('offers', (row) =>
    ['NOT_SENT', 'DELIVERY_FAILED'].includes(field(row, 'lifecycleStatus'))
      ? [act(row, 'Новая версия оффера', '/api/v1/offers/{id}/versions', 'offers.manage')]
      : [],
  );
  extend('interviews', (row) => [
    act(
      row,
      'Добавить обратную связь',
      '/api/v1/interviews/{id}/feedback',
      'interviews.feedback.own',
    ),
  ]);
  extend('offboarding', (row) =>
    row.accessExtensionReason && !row.accessDisabledAt
      ? [
          act(
            row,
            'Отменить продление доступа',
            '/api/v1/offboarding-processes/{id}/access-extension',
            'offboarding.access.manage',
            'DELETE',
            'remove-access-extension',
          ),
        ]
      : [],
  );
  extend('offboarding', (row) =>
    ['PENDING', 'IN_PROGRESS'].includes(field(row, 'status')) && !row.accessDisabledAt
      ? [
          act(
            row,
            'Отключить доступ сейчас',
            '/api/v1/offboarding-processes/{id}/disable-access-now',
            'offboarding.access.manage',
          ),
        ]
      : [],
  );
  byKey('templates').readPermissions = ['onboarding.read', 'offboarding.read'];
  for (const [key, version] of [
    ['skills', 'skill-versions'],
    ['role-profiles', 'role-profile-versions'],
    ['employee-evaluation-templates', 'employee-evaluation-template-versions'],
  ]) {
    byKey(key!).canEdit = (row) => row.status === 'ACTIVE';
    extend(key!, (row) =>
      field(row, 'status') === 'ACTIVE'
        ? [
            act(
              row,
              'Архивировать',
              `/api/v1/${key}/{id}/archive` as Action['path'],
              'performance_templates.manage',
            ),
          ]
        : [],
    );
    byKey(key!).children = (row) => [versionChildren(row, key!, version!)];
    resources.push({
      key: version!,
      section: 'performance',
      hidden: true,
      parentScoped: key!,
      title: 'Версии',
      singular: 'Версия',
      path: `/api/v1/${key}/{id}/versions` as GetPath,
      detail: `/api/v1/${version}/{id}` as GetPath,
      edit: `/api/v1/${version}/{id}` as Action['path'],
      canEdit: (row) => allowed(row, 'edit'),
      readPermission: 'performance_templates.read',
      managePermission: 'performance_templates.manage',
      fields: ['versionNumber', 'status', 'content', 'publishedAt'],
      description: 'Содержимое версии',
      actions: (row) =>
        row.status === 'DRAFT' && allowed(row, 'publish')
          ? [
              act(
                row,
                'Опубликовать версию',
                `/api/v1/${version}/{id}/publish` as Action['path'],
                'performance_templates.manage',
              ),
            ]
          : row.status === 'PUBLISHED' && allowed(row, 'retire')
            ? [
                act(
                  row,
                  'Вывести версию из использования',
                  `/api/v1/${version}/{id}/retire` as Action['path'],
                  'performance_templates.manage',
                ),
              ]
            : [],
    });
  }
  const cycles = byKey('cycles');
  cycles.readPermission = 'performance_reviews.manage';
  cycles.readPermissions = ['performance_reviews.manage', 'work_plans.manage'];
  cycles.managePermission = 'performance_reviews.manage';
  cycles.edit = '/api/v1/performance-cycles/{id}';
  cycles.canEdit = (row) => ['DRAFT', 'ACTIVE'].includes(field(row, 'status'));
  cycles.actions = (row) =>
    [
      ['activate', 'Открыть цикл'],
      ['close', 'Закрыть цикл'],
      ['cancel', 'Отменить цикл'],
    ]
      .filter(([key]) => allowed(row, key!))
      .map(([key, label]) =>
        act(
          row,
          label!,
          `/api/v1/performance-cycles/{id}/${key}` as Action['path'],
          'performance_reviews.manage',
        ),
      );
  const plans = byKey('work-plans');
  plans.edit = '/api/v1/work-plans/{id}';
  plans.canEdit = (row) => allowed(row, 'edit');
  plans.actions = (row) => [
    ...(allowed(row, 'activate')
      ? [act(row, 'Активировать план', '/api/v1/work-plans/{id}/activate', 'work_plans.manage')]
      : []),
    ...(allowed(row, 'cancel')
      ? [act(row, 'Отменить план', '/api/v1/work-plans/{id}/cancel', 'work_plans.manage')]
      : []),
    ...(field(row, 'status') === 'DRAFT'
      ? [act(row, 'Добавить задачу', '/api/v1/work-plans/{id}/tasks', 'work_plans.manage')]
      : []),
    ...(allowed(row, 'start-review')
      ? [act(row, 'Начать оценку', '/api/v1/work-plans/{id}/reviews', 'performance_reviews.manage')]
      : []),
  ];
  plans.children = (row) => [
    {
      key: 'tasks',
      title: 'Задачи',
      path: '/api/v1/work-plans/{id}/tasks',
      params: { id: row.id },
      fields: ['definition', 'status', 'dueDate', 'result'],
      targetResource: 'work-tasks',
    },
  ];
  resources.push({
    key: 'work-tasks',
    section: 'performance',
    hidden: true,
    parentScoped: 'work-plans',
    title: 'Задачи',
    singular: 'Рабочая задача',
    path: '/api/v1/work-plans/{id}/tasks',
    readPermission: 'work_plans.read',
    readPermissions: ['work_plans.read', 'work_plans.manage'],
    managePermission: 'work_plans.manage',
    fields: ['definition', 'status', 'dueDate', 'result', 'evidence', 'reason'],
    description: 'Задача из рабочего плана',
    actions: (row) =>
      [
        ['edit', 'Изменить задачу', 'PATCH', 'work_plans.manage', ''],
        ['progress', 'Обновить результат', 'PATCH', 'work_plans.update_own', '/progress'],
        ['start', 'Начать работу', 'POST', 'work_plans.update_own', '/start'],
        ['submit', 'Передать на проверку', 'POST', 'work_plans.update_own', '/submit'],
        ['accept', 'Принять результат', 'POST', 'work_plans.manage', '/accept'],
        ['return', 'Вернуть на доработку', 'POST', 'work_plans.manage', '/return'],
        ['exclude', 'Исключить задачу', 'POST', 'work_plans.manage', '/exclude'],
      ]
        .filter(([key]) => allowed(row, key!))
        .map(([key, label, method, permission, suffix]) =>
          act(
            row,
            label!,
            `/api/v1/work-tasks/{id}${suffix}` as Action['path'],
            permission!,
            method as Action['method'],
            key,
          ),
        ),
  });
  const reviews = byKey('reviews');
  reviews.actions = (row) =>
    [
      ['open', 'Открыть оценку', 'performance_reviews.manage'],
      ['reviewer', 'Назначить руководителя', 'performance_reviews.manage'],
      ['finalize', 'Итоговая оценка', 'performance_reviews.publish'],
      ['publish', 'Опубликовать результат', 'performance_reviews.publish'],
      ['cancel', 'Отменить оценку', 'performance_reviews.manage'],
      ['corrections', 'Исправить опубликованную оценку', 'performance_reviews.manage'],
    ]
      .filter(([key]) => allowed(row, key!))
      .map(([key, label, permission]) =>
        act(row, label!, `/api/v1/reviews/{id}/${key}` as Action['path'], permission!),
      );
  resources.push({
    key: 'service-accounts',
    section: 'admin',
    title: 'Сервисные учётные записи',
    singular: 'Сервисная учётная запись',
    path: '/api/v1/service-accounts',
    create: '/api/v1/service-accounts',
    readPermission: 'service_accounts.manage',
    managePermission: 'service_accounts.manage',
    fields: ['name', 'status', 'expiresAt', 'grants'],
    description: 'Доступ интеграций к платформе',
    actions: (row) =>
      field(row, 'status') === 'ACTIVE'
        ? [
            act(
              row,
              'Обновить ключ',
              '/api/v1/service-accounts/{id}/rotate',
              'service_accounts.manage',
            ),
            act(
              row,
              'Отозвать доступ',
              '/api/v1/service-accounts/{id}/revoke',
              'service_accounts.manage',
            ),
          ]
        : [],
  });
  resources.push({
    key: 'retention-policies',
    section: 'admin',
    title: 'Хранение данных',
    singular: 'Правило хранения',
    path: '/api/v1/retention-policies',
    create: '/api/v1/retention-policies',
    edit: '/api/v1/retention-policies/{id}',
    readPermission: 'retention.manage',
    managePermission: 'retention.manage',
    fields: ['dataType', 'purpose', 'jurisdiction', 'durationDays', 'action', 'status'],
    description: 'Сроки хранения персональных данных',
    actions: (row) => [
      {
        ...act(
          row,
          'Проверить применение правил',
          '/api/v1/retention-runs/dry-run',
          'retention.manage',
        ),
        params: {},
        initial: { policyId: row.id, asOf: new Date().toISOString() },
      },
    ],
  });
  resources.push({
    key: 'legal-holds',
    section: 'admin',
    title: 'Приостановка удаления',
    singular: 'Приостановка удаления',
    path: '/api/v1/legal-holds',
    create: '/api/v1/legal-holds',
    readPermission: 'legal_holds.manage',
    managePermission: 'legal_holds.manage',
    fields: ['entityType', 'reason', 'status', 'createdAt', 'releasedAt'],
    description: 'Приостанавливайте удаление данных по юридическому основанию',
    actions: (row) =>
      field(row, 'status') === 'ACTIVE'
        ? [act(row, 'Снять приостановку', '/api/v1/legal-holds/{id}/release', 'legal_holds.manage')]
        : [],
  });
}
